// Free licenses fixtures. Michael's Signature plan earns him an Essentials license each month to
// give away, and the licensed demo is that giver part way through: three licenses to give, two
// invitations waiting, four people who accepted, one of whom has since taken a plan of his own.
// `?demo=locked` runs the persona without the paid capabilities, so there his seat is an Essentials
// one David's plan extends (`demoSponsoredSeat`, which the identity fixtures serve as his license),
// and Free licenses answers as the recipient's page.
//
// Mutable like the payment-method fixtures, so an invitation sent or sent again, and a share link
// turned on or off, reads back on the next request.

import type { GiftDependent } from 'pb-billing-ui/gift-dependents.ts';
import type { DemoRequest, DemoRoute } from '../types';
import { envelope, refusal } from '../envelope';
import { isDemoPlanLocked } from '../mode';
import { DEMO_PERSON_ID, DEMO_PERSONA, demoDaysAgo, demoDaysAhead } from '../persona';
import { LicenseRenewalTerm, LicenseType } from '~/types/licenses/licenses';
import type {
    LicenseGiftInvitation,
    LicenseGiftInvitationInput,
    LicenseGiftRecipient,
    LicenseGiftsOverview,
    LicenseGiftShareLink,
    PublicLicenseGift,
} from '~/types/person/license-gifts';
import type { License } from '~/types/person/person';

/** The member whose plan extends Michael's seat in the locked demo, and who invites him on the claim page. */
const DEMO_SPONSOR_FIRST_NAME = 'David';

/** How many days an invitation or a share link works, as app-api mints them. */
const GIFT_LIFETIME_DAYS = 7;

/** A calendar day `days` from now, `YYYY-MM-DD`: the shape a DATE column answers in. */
const demoDayAhead = (days: number): string => demoDaysAhead(days).slice(0, 10);

// Counters rather than random values, so the same actions always produce the same account.
let mintedSequence = 0;
const nextSequence = () => ++mintedSequence;

let credits = 3;

let invitations: LicenseGiftInvitation[] = [
    { id: 88102, first_name: 'Pam', email: 'pam.beesly@example.com', sent_at: demoDaysAgo(2), expires_at: demoDaysAhead(5) },
    { id: 88101, first_name: 'Ryan', email: 'ryan.howard@example.com', sent_at: demoDaysAgo(6), expires_at: demoDaysAhead(1) },
];

/** The first to accept, who has since taken a plan of his own, so the giver's plan no longer protects him. */
const JIM: LicenseGiftRecipient = { id: 88001, first_name: 'Jim', masked_email: 'j***@example.com', accepted_at: demoDaysAgo(103) };

// The first to accept first, as app-api lists them.
const RECIPIENTS: LicenseGiftRecipient[] = [
    JIM,
    { id: 88002, first_name: 'Dwight', masked_email: 'd***@example.com', accepted_at: demoDaysAgo(74) },
    { id: 88003, first_name: 'Holly', masked_email: 'h***@example.com', accepted_at: demoDaysAgo(41) },
    { id: 88004, first_name: 'Erin', masked_email: 'e***@example.com', accepted_at: demoDaysAgo(9) },
];

/** The people the giver's plan protects now, in the recipients' order: everyone but Jim. */
const DEPENDENTS: GiftDependent[] = RECIPIENTS.filter((recipient) => recipient !== JIM).map(({ first_name, masked_email, accepted_at }) => ({
    first_name,
    masked_email,
    accepted_at,
}));

let shareLink: LicenseGiftShareLink | null = null;

function sponsorOverview(): LicenseGiftsOverview {
    return {
        visible: true,
        eligible: true,
        credits,
        next_credit_at: demoDayAhead(12),
        share_link: shareLink,
        invitations,
        recipients: RECIPIENTS,
        dependents: DEPENDENTS,
        dependents_count: DEPENDENTS.length,
        sponsorship_ends_at: null,
        sponsored_by: null,
    };
}

function recipientOverview(): LicenseGiftsOverview {
    return {
        visible: true,
        eligible: false,
        credits: 0,
        next_credit_at: null,
        share_link: null,
        invitations: [],
        recipients: [],
        dependents: [],
        dependents_count: 0,
        sponsorship_ends_at: null,
        sponsored_by: { first_name: DEMO_SPONSOR_FIRST_NAME, grace_ends_at: null },
    };
}

function respondOverview() {
    return envelope(isDemoPlanLocked() ? recipientOverview() : sponsorOverview());
}

function respondInvite(req: DemoRequest) {
    const body = (req.body ?? {}) as Partial<LicenseGiftInvitationInput>;
    const sent: LicenseGiftInvitation = {
        id: 88200 + nextSequence(),
        first_name: body.first_name ?? '',
        email: body.email ?? '',
        sent_at: demoDaysAgo(0),
        expires_at: demoDaysAhead(GIFT_LIFETIME_DAYS),
    };

    invitations = [sent, ...invitations];
    credits = Math.max(0, credits - 1);

    return envelope(sent);
}

function invitationId(req: DemoRequest): number {
    return Number(req.match[1]);
}

function respondResend(req: DemoRequest) {
    const id = invitationId(req);
    invitations = invitations.map((held) =>
        held.id === id ? { ...held, sent_at: demoDaysAgo(0), expires_at: demoDaysAhead(GIFT_LIFETIME_DAYS) } : held,
    );

    return null;
}

function respondOpenShareLink() {
    shareLink = {
        url: `${window.location.origin}/claim/demo-share-link-${String(nextSequence()).padStart(4, '0')}`,
        expires_at: demoDaysAhead(GIFT_LIFETIME_DAYS),
    };

    return envelope(shareLink);
}

function respondCloseShareLink() {
    shareLink = null;

    return null;
}

/** Michael's seat in the locked demo: Essentials, extended by David's plan. */
export function demoSponsoredSeat(): License {
    return {
        id: 55502,
        msrp_price: 0,
        price: 0,
        discounts: [],
        type: 'Essentials',
        expired: false,
        expires: false,
        renew: false,
        renews: '',
        next_bill_date: '',
        display: 'Essentials',
        refundable: false,
        prorate_amount: 0,
        assigned_to: { id: DEMO_PERSON_ID, first_name: DEMO_PERSONA.first_name, last_name: DEMO_PERSONA.last_name },
        slug: LicenseType.ESSENTIALS,
        remediation_type: 'standard',
        term: LicenseRenewalTerm.MONTHLY,
        sponsored: true,
        sponsor_first_name: DEMO_SPONSOR_FIRST_NAME,
        owner: { id: DEMO_PERSON_ID, type: 1, slug: 'individual', name: DEMO_PERSONA.full_name },
        scheduled_change: null,
    };
}

/**
 * Either persona already holds a live seat, Signature of his own or the Essentials one David's plan
 * extends, so a claim is refused the way app-api refuses a member who is already covered.
 */
function respondClaim() {
    return refusal('me/license-gifts/claim', 422, 'You already have Privacy Bee protection.', { reason: 'holder_has_live_license' });
}

function respondPublicGift() {
    const gift: PublicLicenseGift = {
        sponsor_first_name: DEMO_SPONSOR_FIRST_NAME,
        kind: 'invite',
        available: true,
        reason: null,
        expires_at: demoDaysAhead(5),
    };

    return envelope(gift);
}

export const licenseGiftRoutes: DemoRoute[] = [
    { method: 'GET', pattern: /^me\/license-gifts$/, respond: respondOverview },

    // Invitations: the one invitation's routes (specific) before the collection's.
    { method: 'POST', pattern: /^me\/license-gifts\/invitations\/(\d+)\/resend$/, status: 204, respond: respondResend },
    { method: 'POST', pattern: /^me\/license-gifts\/invitations$/, status: 201, respond: respondInvite },

    { method: 'PUT', pattern: /^me\/license-gifts\/share-link$/, respond: respondOpenShareLink },
    { method: 'DELETE', pattern: /^me\/license-gifts\/share-link$/, status: 204, respond: respondCloseShareLink },

    { method: 'POST', pattern: /^me\/license-gifts\/claim$/, status: 422, respond: respondClaim },

    { method: 'GET', pattern: /^public\/license-gifts\/[^/]+$/, respond: respondPublicGift },
];
